/**
 * useUrlListState — list-view query state (filters, search, page) held in the URL.
 *
 * WHY THE URL AND NOT A STORE: a list page unmounts when you open a row, so any
 * state kept in `useState` is gone by the time you come back. The two candidate
 * homes for it are a cached store or the query string; this codebase needs the
 * query string for three reasons beyond "it survives":
 *
 *   1. Deep links already exist. Campaign Run History links into Contacts with
 *      `?advanced=…&campaignId=…`. With the URL as the source of truth a deep
 *      link IS the state, so there is no "clicked link vs. remembered filter"
 *      conflict to arbitrate.
 *   2. A filtered view becomes shareable and bookmarkable.
 *   3. Cached filters are invisible: a user returning to a silently-filtered
 *      list reads it as missing data. The query string makes the state legible.
 *
 * AND IT IS FEWER REQUESTS, NOT MORE. State hydrated from a store inside an
 * effect renders once with defaults (fetch #1) and again once hydrated
 * (fetch #2). Parsing `searchParams` in a lazy `useState` initializer means the
 * very first render already holds the real query, so a mount — deep link
 * included — costs exactly one fetch. Nothing here re-derives state from the
 * URL after mount, so a URL write can never feed back into a refetch.
 *
 * Ephemeral view state (scroll offset, expanded rows, selection) deliberately
 * does NOT belong here — it is verbose, meaningless to a reader, and unsharable.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { rememberListQuery } from '@/utils/admin/lastListQuery';

/** How one field moves between a typed value and its query-string form. */
export interface FieldCodec<T> {
  defaultValue: T;
  /** Query-string form, or `null` to omit the param (always so for defaults). */
  encode: (value: T) => string | null;
  /** Parse one raw query value; `undefined` falls back to `defaultValue`. */
  decode: (raw: string) => T | undefined;
  /** Marks the pagination field — the one key a filter change resets. */
  isPageField?: boolean;
}

export type UrlFieldSpec = Record<string, FieldCodec<any>>;

/** Lets a URL-backed setter accept React's `(prev) => next` updater form, so a
 *  call site converted from `useState` keeps the shape it already had. */
export type Updater<T> = T | ((prev: T) => T);

export function resolveUpdate<T>(update: Updater<T>, prev: T): T {
  return typeof update === 'function' ? (update as (p: T) => T)(prev) : update;
}

export type UrlState<S extends UrlFieldSpec> = {
  [K in keyof S]: S[K] extends FieldCodec<infer T> ? T : never;
};

/**
 * Builds a codec that omits its default from the URL, so an unfiltered list
 * sits at a clean `/admin/contacts` rather than a wall of `?status=all&…`.
 */
function make<T>(
  defaultValue: T,
  enc: (value: T) => string,
  dec: (raw: string) => T | undefined,
  isPageField = false
): FieldCodec<T> {
  const defaultEncoded = enc(defaultValue);
  return {
    defaultValue,
    encode: (value) => {
      const encoded = enc(value);
      return encoded === defaultEncoded ? null : encoded;
    },
    decode: dec,
    isPageField,
  };
}

/**
 * Field builders. A page declares its query shape with these instead of
 * hand-writing an encode/decode pair per field — that per-page codec is the
 * cost that made URL state look expensive to roll out across nine list pages.
 */
export const field = {
  /** Free text passed through verbatim (search boxes, ids, ISO dates). */
  str: (defaultValue = '') => make<string>(defaultValue, (v) => v, (raw) => raw),

  /** Rejects any value outside `values`, so a hand-edited URL cannot inject one. */
  enumOf: <T extends string>(values: readonly T[], defaultValue: T) =>
    make<T>(
      defaultValue,
      (v) => v,
      (raw) => (values.includes(raw as T) ? (raw as T) : undefined)
    ),

  /** Comma-separated list — readable in the URL, unlike repeated keys or JSON. */
  csv: (defaultValue: string[] = []) =>
    make<string[]>(
      defaultValue,
      (v) => v.join(','),
      (raw) =>
        raw
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
    ),

  /**
   * Structured values with no readable flat form. `isValid` guards the parse so
   * malformed JSON in a URL falls back to the default rather than reaching
   * render as a wrong-shaped object.
   */
  json: <T>(defaultValue: T, isValid: (value: unknown) => value is T) =>
    make<T>(
      defaultValue,
      (v) => JSON.stringify(v),
      (raw) => {
        try {
          const parsed: unknown = JSON.parse(raw);
          return isValid(parsed) ? parsed : undefined;
        } catch {
          return undefined;
        }
      }
    ),

  /** A toggle. Present in the URL only when it differs from the default. */
  bool: (defaultValue = false) =>
    make<boolean>(
      defaultValue,
      (v) => (v ? '1' : '0'),
      (raw) => (raw === '1' || raw === 'true' ? true : raw === '0' || raw === 'false' ? false : undefined)
    ),

  /** 1-based page number. Exactly one field per spec should be this. */
  page: () =>
    make<number>(
      1,
      (v) => String(v),
      (raw) => {
        const parsed = parseInt(raw, 10);
        return Number.isInteger(parsed) && parsed >= 1 ? parsed : undefined;
      },
      true
    ),

  /**
   * 0-based page index, for the MUI `TablePagination` pages that count from
   * zero. The URL still carries the human number (`?page=3` is the third page),
   * so a link means the same thing no matter how the page indexes internally.
   */
  pageIndex: () =>
    make<number>(
      0,
      (v) => String(v + 1),
      (raw) => {
        const parsed = parseInt(raw, 10);
        return Number.isInteger(parsed) && parsed >= 1 ? parsed - 1 : undefined;
      },
      true
    ),
};

/** Typed `Object.entries` over a spec — avoids indexing, which under
 *  `noUncheckedIndexedAccess` widens every codec to `| undefined`. */
function entriesOf<S extends UrlFieldSpec>(spec: S): Array<[string, FieldCodec<any>]> {
  return Object.entries(spec);
}

/** Parses a whole spec out of a query string, falling back per field. */
export function decodeAll<S extends UrlFieldSpec>(spec: S, params: URLSearchParams): UrlState<S> {
  const out = {} as UrlState<S>;
  for (const [key, codec] of entriesOf(spec)) {
    const raw = params.get(key);
    const parsed = raw === null ? undefined : codec.decode(raw);
    (out as any)[key] = parsed === undefined ? codec.defaultValue : parsed;
  }
  return out;
}

/**
 * Two values are the same query iff they encode identically — exactly the
 * equality that matters here, and it avoids a bespoke deep-compare.
 */
function sameField(codec: FieldCodec<any>, a: unknown, b: unknown): boolean {
  return codec.encode(a) === codec.encode(b);
}

function sameQuery<S extends UrlFieldSpec>(spec: S, a: UrlState<S>, b: UrlState<S>): boolean {
  return entriesOf(spec).every(([key, codec]) => sameField(codec, a[key], b[key]));
}

/**
 * Merges a patch, applying the page-reset rule. Returns `prev` UNCHANGED (same
 * reference) when nothing the URL cares about moved — that identity is what lets
 * React bail out of the render, so a no-op patch costs no refetch.
 */
export function mergeQuery<S extends UrlFieldSpec>(
  spec: S,
  prev: UrlState<S>,
  changes: Partial<UrlState<S>>
): UrlState<S> {
  const merged = { ...prev, ...changes } as UrlState<S>;
  const entries = entriesOf(spec);
  const pageEntry = entries.find(([, codec]) => codec.isPageField);
  const pageKey = pageEntry?.[0];

  // Page resets only when a filter's VALUE actually moved — not merely because
  // a filter key appeared in the patch. Without that distinction a no-op patch
  // (the search debounce firing on mount with the value it just read out of the
  // URL) would knock a deep link to ?page=5 back to page 1.
  const filtersMoved = entries.some(
    ([key, codec]) => key !== pageKey && !sameField(codec, prev[key], merged[key])
  );
  if (filtersMoved && pageEntry && !(pageEntry[0] in changes)) {
    (merged as any)[pageEntry[0]] = pageEntry[1].defaultValue;
  }

  return sameQuery(spec, prev, merged) ? prev : merged;
}

/**
 * Projects state onto a query string. Params outside the spec are preserved, so
 * this never clobbers a key some other feature owns.
 */
export function applyToParams<S extends UrlFieldSpec>(
  spec: S,
  state: UrlState<S>,
  current: URLSearchParams
): URLSearchParams {
  const next = new URLSearchParams(current);
  for (const [key, codec] of entriesOf(spec)) {
    const encoded = codec.encode(state[key]);
    if (encoded === null) next.delete(key);
    else next.set(key, encoded);
  }
  return next;
}

export interface UseUrlListState<S extends UrlFieldSpec> {
  /**
   * The committed query. Identity is stable until the query actually changes,
   * so it is safe to use directly as a fetch effect's dependency.
   */
  state: UrlState<S>;
  /** Merge a partial change. Changing any non-page field returns to page 1. */
  patch: (changes: Partial<UrlState<S>>) => void;
  /** Back to every default (an empty query string). */
  reset: () => void;
  /** True when anything differs from the defaults. */
  isDirty: boolean;
}

/**
 * `spec` is captured on first render and treated as constant — declare it at
 * module scope. A per-render spec object would otherwise re-key every codec
 * identity on each render.
 */
export function useUrlListState<S extends UrlFieldSpec>(spec: S): UseUrlListState<S> {
  const specRef = useRef(spec);
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  /** The query string this hook last put in the URL. Anything else showing up
   *  there came from outside — the sidebar, back/forward, a link into this
   *  route — and should be adopted rather than overwritten. */
  const lastWritten = useRef<string | null>(null);

  // Read synchronously on the FIRST render. Hydrating in an effect instead is
  // precisely what makes a list fetch twice on mount.
  const [state, setState] = useState<UrlState<S>>(() => decodeAll(specRef.current, searchParams));

  const patch = useCallback((changes: Partial<UrlState<S>>) => {
    setState((prev) => mergeQuery(specRef.current, prev, changes));
  }, []);

  const reset = useCallback(() => {
    setState((prev) => {
      const defaults = decodeAll(specRef.current, new URLSearchParams());
      return sameQuery(specRef.current, prev, defaults) ? prev : defaults;
    });
  }, []);

  // Keeps state and URL in agreement. Writing is the normal direction; reading
  // back happens ONLY when the URL moved without us. That is what makes an
  // external navigation to this same route take effect — the sidebar clearing
  // filters, or back/forward between two queries — instead of being silently
  // overwritten by the state already held in memory.
  useEffect(() => {
    const current = searchParams.toString();
    const encoded = applyToParams(specRef.current, state, searchParams).toString();

    if (encoded === current) {
      // Already in agreement. The mount pass lands here, so it costs nothing.
      lastWritten.current = current;
      rememberListQuery(pathname, current);
      return;
    }

    if (lastWritten.current !== null && current !== lastWritten.current) {
      // Someone else navigated. Adopt the URL rather than fight it; this
      // terminates, because the next pass finds `encoded === current`.
      setState(decodeAll(specRef.current, searchParams));
      lastWritten.current = current;
      return;
    }

    lastWritten.current = encoded;
    // `replace` keeps filter tweaks out of the history stack, so Back still
    // means "leave this page" rather than "undo one filter".
    setSearchParams(encoded, { replace: true });
  }, [state, searchParams, setSearchParams, pathname]);

  const isDirty = useMemo(() => {
    const activeSpec = specRef.current;
    return entriesOf(activeSpec).some(([key, codec]) => codec.encode(state[key]) !== null);
  }, [state]);

  return { state, patch, reset, isDirty };
}
